import { Button, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Divider from "components/Divider/Divider";
import { Controller } from "react-hook-form";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import {
  CustomComponentCommonProps,
  pickTheFieldsFrom,
  CONFIG_KEYS,
  filterDifferences,
  rules,
} from "../utils";
import CheckboxInput from "components/Input/CheckboxInput/CheckboxInput";
import Timepicker from "components/Input/Timepicker/Timepicker";
import ImageUploader from "../Component/ImageUploader";
import { useTranslation } from "react-i18next";
import { updateMerchant, updateMerchantOtherInfo } from "../model";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
const MerchantConfig = ({
  merchant,
  merchantOtherDetail,
  fetchMerchantAndMerchantOtherDetail,
  staticMerchantPaylaod,
}) => {
  const { t } = useTranslation();
  const PAGE_LOCALE_KEY = "page.merchants.";
  const FORM_LABEL_KEY = PAGE_LOCALE_KEY + "merchantDetail.config.";
  const { control, handleSubmit, reset, setValue, setError, clearErrors } =
    useForm({
      mode: "onChange",
    });
  const { enqueueSnackbar } = useSnackbar();
  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const onSubmit = async (formValue) => {
    try {
      setIsLoading(true);
      const MERCHANT_UPDATE_IGNORE_FIELDS = [
        "settleCycle",
        "otherInfo",
        "zeroPlan",
      ];
      const MERCHANT_OTHER_INFO_UPDATE_FIELDS = ["settleCycle", "zeroPlan"];
      const MERCHANT_OTHER_INFO_UPDATE_IGNORE_FIELDS = ["otherInfo"];

      const apiConfigs = [
        {
          key: "MERCHANT",
          diff: filterDifferences(
            merchant,
            formValue,
            [],
            MERCHANT_UPDATE_IGNORE_FIELDS,
          ),
          payload: {
            ...staticMerchantPaylaod(merchant, "config", {
              previewUrl: previewUrl,
            }),
            ...filterDifferences(
              merchant,
              formValue,
              [],
              MERCHANT_UPDATE_IGNORE_FIELDS,
            ),
          },
          apiCall: updateMerchant,
        },
        {
          key: "MERCHANT_OTHER_INFO",
          diff: filterDifferences(
            merchantOtherDetail,
            formValue,
            MERCHANT_OTHER_INFO_UPDATE_FIELDS,
            MERCHANT_OTHER_INFO_UPDATE_IGNORE_FIELDS,
          ),
          payload: {
            ...filterDifferences(
              merchantOtherDetail,
              formValue,
              MERCHANT_OTHER_INFO_UPDATE_FIELDS,
              MERCHANT_OTHER_INFO_UPDATE_IGNORE_FIELDS,
            ),
            merchantId: merchant?.merchantId,
            id: merchantOtherDetail?.id,
            ...(merchantOtherDetail?.sales?.id && {
              sales: { id: merchantOtherDetail.sales.id },
            }),
            ...(merchantOtherDetail?.onboardSales?.id && {
              onboardSales: { id: merchantOtherDetail.onboardSales.id },
            }),
            ...(merchantOtherDetail?.referrer?.id && {
              referrer: { id: merchantOtherDetail.referrer.id },
            }),
          },
          apiCall: updateMerchantOtherInfo,
        },
      ];
      const validApis = apiConfigs.filter((config) => {
        console.log(`${config.key} config.diff`, config.diff);
        return Object.keys(config.diff).length > 0;
      });
      if (validApis.length === 0) {
        snackbar(
          t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.noChanges"),
          "info",
        );
        return;
      }

      const [responses, errors] = await Promise.allSettled(
        validApis.map((config) => config.apiCall(config.payload)),
      ).then((results) => {
        const res = {};
        const errs = {};

        results.forEach((result, index) => {
          console.log("result", result);
          const key = validApis[index].key;
          if (result.status === "fulfilled") {
            res[key] = result.value;
          } else {
            errs[key] = result.reason;
            console.error(`${key} error:`, result.reason);
          }
        });

        return [res, errs];
      });

      let hasError = false;

      validApis.forEach(({ key }) => {
        const response = responses[key];
        const error = errors[key];
        if (error) {
          hasError = true;
          return snackbar(
            `${t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.updateFailed")}: ${error.message}`,
            "error",
          );
        }

        if (response?.status === 401) {
          console.log("Unauthorized access");
          hasError = true;
          return logout();
        }

        if (response?.status === 403) {
          hasError = true;
          return snackbar(t("general.message.unauthorized"), "error");
        }

        const respBody = response?.data;
        if (respBody?.responseCode !== "0000") {
          hasError = true;
          return snackbar(
            `${t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.updateFailed")}: ${
              error?.message || "Unknown error"
            }`,
            "error",
          );
        }
      });

      if (!hasError && validApis.length > 0) {
        snackbar(
          t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.updateSuccess"),
          "success",
        );
      }
      await fetchMerchantAndMerchantOtherDetail();
    } catch (error) {
      console.error("Submission error:", error);
      snackbar(
        `${t(PAGE_LOCALE_KEY + "merchantDetail.fetchMessage.updateFailed")}: ${error.message}`,
        "error",
      );
    } finally {
      setIsLoading(false);
      setIsEdit(false);
    }
  };
  const appendBase64Data = (base64String) => {
    if (base64String?.startsWith("iVBORw0KGgo")) {
      base64String = "data:image/png;base64," + base64String;
    }
    if (base64String?.startsWith("/9j/")) {
      base64String = "data:image/jpeg;base64," + base64String;
    }
    return base64String;
  };

  useEffect(() => {
    if (Object.keys(merchant).length > 0) {
      // console.log("merchant", merchant);
      const initialValue = {
        ...pickTheFieldsFrom(merchant, CONFIG_KEYS()),
        ...pickTheFieldsFrom(merchantOtherDetail, CONFIG_KEYS()),
      };
      setAllValues(initialValue);
    }
  }, [merchant]);

  const setAllValues = (initialValue) => {
    reset(
      {
        ...initialValue,
        isKDollar: !!initialValue?.otherInfo?.campaign?.find(
          (campaign) => campaign === "KDOLLAR",
        ),
        merchantLogo: appendBase64Data(initialValue?.merchantLogo),
      },
      {
        keepDefaultValues: false,
        keepDirty: false,
        keepErrors: false,
      },
    );
  };

  return (
    <Stack direction="column" sx={{ padding: "24px", background: "white" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction={"column"} rowGap={2} width={"80%"}>
          <Typography variant="h3">
            {t(`${FORM_LABEL_KEY}report.title`)}
          </Typography>
          <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            <Controller
              name="autoTransactionReceipt"
              control={control}
              defaultValue=""
              render={({ field: { value, onChange }, fieldState: {} }) => (
                <CheckboxInput
                  {...CustomComponentCommonProps(
                    t(`${FORM_LABEL_KEY}report.fields.autoTransactionReceipt`),
                    value,
                    "CheckboxInput",
                    false,
                    isEdit,
                    isLoading,
                  )}
                  oncheckFn={onChange}
                  divStyle={{ flexGrow: 1, flexBasis: "600px" }}
                />
              )}
            />
            <Controller
              name="autoSettlementReport"
              control={control}
              defaultValue=""
              render={({ field: { value, onChange } }) => (
                <CheckboxInput
                  {...CustomComponentCommonProps(
                    t(`${FORM_LABEL_KEY}report.fields.autoSettlementReport`),
                    value,
                    "CheckboxInput",
                    false,
                    isEdit,
                    isLoading,
                  )}
                  oncheckFn={onChange}
                  divStyle={{ flexGrow: 1, flexBasis: "600px" }}
                />
              )}
            />
            <Controller
              name="autoDailyReport"
              control={control}
              defaultValue=""
              render={({ field: { value, onChange } }) => (
                <CheckboxInput
                  {...CustomComponentCommonProps(
                    t(`${FORM_LABEL_KEY}report.fields.autoDailyReport`),
                    value,
                    "CheckboxInput",
                    false,
                    isEdit,
                    isLoading,
                  )}
                  oncheckFn={onChange}
                  divStyle={{
                    flexGrow: 1,
                    flexBasis: "600px",
                    marginBottom: "18px",
                  }}
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack direction={"column"} rowGap={2} width={"80%"}>
          <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            <Controller
              name="summaryReportTime"
              control={control}
              defaultValue=""
              render={({ field: { value, onChange }, fieldState: {} }) => (
                <Timepicker
                  sxParams={{
                    datepickerFocus: "",
                    datepickerError: "",
                  }}
                  onchangeFn={onChange}
                  {...CustomComponentCommonProps(
                    t(`${FORM_LABEL_KEY}report.fields.summaryReportTime`),
                    value,
                    "Timepicker",
                    false,
                    isEdit,
                    isLoading,
                  )}
                  divStyle={{ flexGrow: 1, flexBasis: "400px" }}
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack direction={"column"} rowGap={2} width={"80%"}>
          <Typography variant="h3">
            {t(`${FORM_LABEL_KEY}financeAndSettlement.title`)}
          </Typography>
          <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            <Controller
              name="settleCycle"
              control={control}
              // defaultValue=""
              render={({ field: { value, onChange } }) => (
                <OutlinedText
                  errorMessage={"page.finance.refund.list.id.error.message"}
                  onchangeFn={onChange}
                  divStyle={{ flexGrow: 1, flexBasis: "600px" }}
                  {...CustomComponentCommonProps(
                    t(
                      `${FORM_LABEL_KEY}financeAndSettlement.fields.settleCycle`,
                    ),
                    value,
                    "OutlinedText",
                    false,
                    isEdit,
                    isLoading,
                  )}
                />
              )}
            />
          </Stack>
          <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            <Controller
              name="smallTicketCharge"
              control={control}
              defaultValue=""
              render={({ field: { value, onChange }, fieldState: {} }) => (
                <CheckboxInput
                  {...CustomComponentCommonProps(
                    t(
                      `${FORM_LABEL_KEY}financeAndSettlement.fields.smallTicketCharge`,
                    ),
                    value,
                    "CheckboxInput",
                    false,
                    isEdit,
                    isLoading,
                  )}
                  oncheckFn={onChange}
                  divStyle={{
                    flexGrow: 1,
                    flexBasis: "300px",
                    marginBottom: "18px",
                  }}
                />
              )}
            />
            <Controller
              name="isBoxsPayMerchant"
              control={control}
              defaultValue=""
              render={({ field: { value, onChange } }) => (
                <CheckboxInput
                  {...CustomComponentCommonProps(
                    t(
                      `${FORM_LABEL_KEY}financeAndSettlement.fields.isBoxsPayMerchant`,
                    ),
                    value,
                    "CheckboxInput",
                    false,
                    isEdit,
                    isLoading,
                  )}
                  oncheckFn={onChange}
                  divStyle={{
                    flexGrow: 1,
                    flexBasis: "300px",
                    marginBottom: "18px",
                  }}
                />
              )}
            />
            <Controller
              name="waiveThermalPaperFees"
              control={control}
              defaultValue=""
              render={({ field: { value, onChange } }) => (
                <CheckboxInput
                  {...CustomComponentCommonProps(
                    t(
                      `${FORM_LABEL_KEY}financeAndSettlement.fields.waiveThermalPaperFees`,
                    ),
                    value,
                    "CheckboxInput",
                    false,
                    isEdit,
                    isLoading,
                  )}
                  oncheckFn={onChange}
                  divStyle={{
                    flexGrow: 1,
                    flexBasis: "300px",
                    marginBottom: "18px",
                  }}
                />
              )}
            />
          </Stack>
          <Controller
            name="zeroPlan"
            control={control}
            defaultValue=""
            render={({ field: { value, onChange } }) => (
              <CheckboxInput
                {...CustomComponentCommonProps(
                  t(`${FORM_LABEL_KEY}financeAndSettlement.fields.zeroPlan`),
                  value,
                  "CheckboxInput",
                  false,
                  isEdit,
                  isLoading,
                )}
                oncheckFn={onChange}
                divStyle={{
                  flexGrow: 1,
                  marginBottom: "18px",
                }}
              />
            )}
          />
          <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            <Controller
              name="merchantRemarks"
              control={control}
              defaultValue=""
              rules={rules({
                maxLength: 255,
                validationTags: ["noLeadingWhitespace", "noQuotesOrBackticks"],
              })}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <OutlinedText
                  errorMessage={error?.message || ""}
                  onchangeFn={onChange}
                  divStyle={{ flexGrow: 1, flexBasis: "600px" }}
                  {...CustomComponentCommonProps(
                    t(
                      `${FORM_LABEL_KEY}financeAndSettlement.fields.merchantRemarks`,
                    ),
                    value,
                    "OutlinedText",
                    false,
                    isEdit,
                    isLoading,
                  )}
                  id={"merchantRemarks"}
                  sxParams={{
                    textError: !!error,
                  }}
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack direction={"column"} rowGap={2} width={"80%"}>
          <Typography variant="h3">
            {t(`${FORM_LABEL_KEY}receipt.title`)}
          </Typography>
          <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            <Controller
              name="merchantLogo"
              control={control}
              defaultValue=""
              render={() => (
                <ImageUploader
                  name={"merchantLogo"}
                  control={control}
                  setError={setError}
                  clearErrors={clearErrors}
                  isEdit={isEdit}
                  description={`(Maximum image size: 1MB; Format: jpg and png only)`}
                  setValue={setValue}
                  previewUrl={previewUrl}
                  setPreviewUrl={setPreviewUrl}
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack direction={"column"} rowGap={2} width={"80%"}>
          <Typography variant="h3">
            {t(`${FORM_LABEL_KEY}campaign.title`)}
          </Typography>
          <Stack direction={"row"} flexWrap={"wrap"}>
            <Controller
              name="isKDollar"
              control={control}
              render={({ field: { value, onChange }, fieldState: {} }) => (
                <CheckboxInput
                  {...CustomComponentCommonProps(
                    t(`${FORM_LABEL_KEY}campaign.fields.isKDollar`),
                    value,
                    "CheckboxInput",
                    false,
                    isEdit,
                    isLoading,
                  )}
                  oncheckFn={onChange}
                  divStyle={{
                    flexGrow: 1,
                    flexBasis: "600px",
                    marginBottom: "24px",
                  }}
                />
              )}
            />
            <Controller
              name="enableCampaign"
              control={control}
              render={({ field: { value, onChange }, fieldState: {} }) => (
                <CheckboxInput
                  {...CustomComponentCommonProps(
                    t(`${FORM_LABEL_KEY}campaign.fields.enableCampaign`),
                    value,
                    "CheckboxInput",
                    false,
                    isEdit,
                    isLoading,
                  )}
                  oncheckFn={onChange}
                  divStyle={{
                    flexGrow: 1,
                    flexBasis: "600px",
                    marginBottom: "24px",
                  }}
                />
              )}
            />
          </Stack>
        </Stack>
        <Stack direction={"column"} rowGap={2} width={"80%"}>
          <Typography variant="h3">
            {t(`${FORM_LABEL_KEY}others.title`)}
          </Typography>
          <Stack direction={"column"} flexWrap={"wrap"} gap={2}>
            <Controller
              name="cooldownTime"
              control={control}
              defaultValue=""
              render={({ field: { value, onChange } }) => (
                <OutlinedText
                  errorMessage={"page.finance.refund.list.id.error.message"}
                  onchangeFn={onChange}
                  // divStyle={{ flexGrow: 1, flexBasis: "400px" }}
                  {...CustomComponentCommonProps(
                    t(`${FORM_LABEL_KEY}others.fields.cooldownTime`),
                    value,
                    "OutlinedText",
                    false,
                    isEdit,
                    isLoading,
                  )}
                />
              )}
            />
            <Controller
              name="monthlyTransLimit"
              control={control}
              rules={rules({
                validationTags: ["onlyPositiveNumber"],
              })}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <OutlinedText
                  errorMessage={error?.message || ""}
                  sxParams={{
                    textError: !!error,
                  }}
                  onchangeFn={(e) => {
                    const val = e.target.value;
                    onChange(val === "" ? null : val);
                  }}
                  // divStyle={{ flexGrow: 1, flexBasis: "400px" }}
                  {...CustomComponentCommonProps(
                    t(`${FORM_LABEL_KEY}others.fields.monthlyTransLimit`),
                    value,
                    "OutlinedText",
                    false,
                    isEdit,
                    isLoading,
                  )}
                />
              )}
            />
          </Stack>
        </Stack>
        <Divider style={{ width: "80%", margin: "24px 0" }} />
        <Stack
          direction={"row"}
          gap={2}
          flexWrap={"wrap"}
          justifyContent={"space-between"}
        >
          {/* <Button
            variant="normal-button"
            onClick={() => {
              const path = "/transactions/offline-transactions";
              const payload = {
                name: transactions.find((tab) => tab.path === path).tabName,
                path: path,
                state: {
                  merchantId: merchant?.merchantId,
                },
              };
              redirectFn("redirectPage", payload);
            }}
            disabled={loading}
          >
            View Transaction
          </Button> */}
          <div></div>
          <Stack direction={"row"} gap={2} flexWrap={"wrap"}>
            {!isEdit ? (
              <Button
                variant="normal-button"
                onClick={() => {
                  setIsEdit(!isEdit);
                }}
                disabled={isLoading}
              >
                {t(`${PAGE_LOCALE_KEY}merchantDetail.button.edit`)}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline-normal-button"
                  onClick={() => {
                    setIsEdit(!isEdit);
                  }}
                  disabled={isLoading}
                >
                  {t(`${PAGE_LOCALE_KEY}merchantDetail.button.cancel`)}
                </Button>
                <LoadingButton
                  type="submit"
                  variant="normal-button"
                  loading={isLoading}
                >
                  {t(`${PAGE_LOCALE_KEY}merchantDetail.button.save`)}
                </LoadingButton>
              </>
            )}
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
};

export default React.memo(MerchantConfig);
